import React, { useState, useMemo } from 'react';
import { MapPin, Plus, Building2, Link as LinkIcon, Edit2, Search, AlertCircle, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { VStack, HStack } from '@/components/ui/Stack';
import { LoadingState } from '@/components/ui/LoadingState';
import { MoveToDestinationModal } from '@/components/venues/MoveToDestinationModal';
import { VenueSearchModal } from './VenueSearchModal';
import { useNotificationStore } from '@/stores/notificationStore';
import { cn } from '@/lib/utils';
import type { VenueMatchResults, CventVenueSummary } from '@/services/CventIntegrationService';
import type { VenueDetails } from '@/services/ResearchService';
import type { Venue } from '@/services/VenuesService';

interface CventVenueMatchingResultsProps {
  eventId: string;
  matchResults: VenueMatchResults;
  destinations: Array<{ id: string; city: string; country: string }>;
  onVenueAdded?: () => void;
  onVenueCreated?: () => void;
  onViewVenueDetails?: (venue: VenueDetails) => void;
}

interface UnconnectedVenueState {
  cventVenue: CventVenueSummary;
  suggestedMatch?: {
    venue: VenueDetails; // Using VenueDetails to match existing prop types
    confidence: number;
    distanceKm?: number;
  };
}

/**
 * Cvent Venue Matching Results Component
 * 
 * Displays unconnected Cvent venues from the RFP and allows:
 * 1. Connecting to a suggested database venue
 * 2. Searching/changing the database venue connection
 * 3. Creating a new venue from Cvent data
 */
export function CventVenueMatchingResults({
  eventId,
  matchResults,
  destinations,
  onVenueAdded,
  onVenueCreated,
  onViewVenueDetails,
}: CventVenueMatchingResultsProps) {
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [hiddenCventIds, setHiddenCventIds] = useState<Set<string>>(new Set());
  
  // Search modal state
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [activeCventId, setActiveCventId] = useState<string | null>(null);
  const [activeCventVenueName, setActiveCventVenueName] = useState<string | null>(null);

  // Destination selection modal state (for creating new venues)
  const [destinationModalOpen, setDestinationModalOpen] = useState(false);
  const [pendingVenueImport, setPendingVenueImport] = useState<{
    cventSupplierId: string;
    venueName: string;
    geocodedLocation: { city: string | null; country: string | null; state: string | null };
  } | null>(null);
  const [isCreatingWithDestination, setIsCreatingWithDestination] = useState(false);
  
  const notificationStore = useNotificationStore();

  // Combine and process unconnected venues
  const unconnectedVenues = useMemo(() => {
    const list: UnconnectedVenueState[] = [];

    // Add venues with matches (In DB Not Shortlisted)
    matchResults.inDbNotShortlisted.forEach(item => {
      if (!hiddenCventIds.has(item.cventVenue.id)) {
        list.push({
          cventVenue: item.cventVenue,
          suggestedMatch: {
            venue: item.venue as unknown as VenueDetails,
            confidence: item.confidence,
            distanceKm: item.distanceKm,
          }
        });
      }
    });

    // Add venues without matches (Not in DB)
    matchResults.notInDb.forEach(cventVenue => {
      if (!hiddenCventIds.has(cventVenue.id)) {
        list.push({
          cventVenue: cventVenue,
        });
      }
    });

    return list;
  }, [matchResults, hiddenCventIds]);

  // Format confidence score
  const formatConfidence = (confidence: number) => {
    return `${Math.round(confidence)}%`;
  };

  // Format distance
  const formatDistance = (distanceKm?: number) => {
    if (distanceKm === undefined || distanceKm === null) return null;
    if (distanceKm < 1) {
      return `${Math.round(distanceKm * 1000)}m`;
    }
    return `${distanceKm.toFixed(1)}km`;
  };

  // Helper to start processing
  const startProcessing = (id: string) => {
    setProcessingIds(prev => new Set(prev).add(id));
  };

  // Helper to stop processing
  const stopProcessing = (id: string) => {
    setProcessingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // 1. Connect to Existing Venue (Suggested or Selected)
  async function handleConnectVenue(cventId: string, venueId: string, venueName: string) {
    startProcessing(cventId);

    try {
      const { CventIntegrationService } = await import('@/services/CventIntegrationService');
      const service = new CventIntegrationService();
      
      await service.addVenueToShortlist({
        eventId,
        venueId,
        cventSupplierId: cventId,
      });

      setHiddenCventIds(prev => new Set(prev).add(cventId));
      
      notificationStore.success(`Connected to ${venueName}`, {
        title: 'Connection Successful',
      });

      onVenueAdded?.();
    } catch (error) {
      console.error('Failed to connect venue:', error);
      notificationStore.error('Failed to connect venue. Please try again.', {
        title: 'Error',
      });
    } finally {
      stopProcessing(cventId);
    }
  }

  // 2. Create New Venue from Cvent
  async function handleCreateVenue(cventId: string, venueName: string) {
    startProcessing(cventId);

    try {
      const { CventIntegrationService } = await import('@/services/CventIntegrationService');
      const service = new CventIntegrationService();
      
      // First, preview to check if destination selection is needed
      const preview = await service.previewCventVenueImport({
        eventId,
        cventSupplierId: cventId,
      });

      if (preview.requiresSelection) {
        // Stop processing here, modal will handle the rest
        setPendingVenueImport({
          cventSupplierId: cventId,
          venueName: preview.venueName || venueName,
          geocodedLocation: preview.geocodedLocation,
        });
        setDestinationModalOpen(true);
        // Note: processing state remains true until modal closes or completes
        return;
      }

      // City matches an existing destination - proceed with auto-allocation
      await service.createVenueFromCvent({
        eventId,
        cventSupplierId: cventId,
      });

      setHiddenCventIds(prev => new Set(prev).add(cventId));
      
      notificationStore.success(`Created and connected ${venueName}`, {
        title: 'Venue Created',
      });

      onVenueCreated?.();
    } catch (error) {
      console.error('Failed to create venue:', error);
      notificationStore.error('Failed to create venue. Please try again.', {
        title: 'Error',
      });
    } finally {
      // Only stop processing if we didn't open the modal
      // If modal opened, processing stops when modal closes/completes
      if (!destinationModalOpen) {
        stopProcessing(cventId);
      }
    }
  }

  // 3. Handle Manual Search Selection
  async function handleSearchSelect(venue: Venue) {
    if (!activeCventId) return;
    
    // Connect the selected venue
    await handleConnectVenue(activeCventId, venue.id, venue.property_name);
    
    setActiveCventId(null);
  }

  // Handle when a venue is imported from search modal
  async function handleVenueImported() {
    // Refresh shortlist - parent component will handle venue match refresh if needed
    onVenueAdded?.();
  }

  // Handle creating venue with explicit destination selection
  async function handleCreateWithDestination(destinationId: string) {
    if (!pendingVenueImport) return;
    
    setIsCreatingWithDestination(true);
    
    try {
      const { CventIntegrationService } = await import('@/services/CventIntegrationService');
      const service = new CventIntegrationService();
      
      await service.createVenueFromCvent({
        eventId,
        cventSupplierId: pendingVenueImport.cventSupplierId,
        destinationId,
      });

      setHiddenCventIds(prev => new Set(prev).add(pendingVenueImport.cventSupplierId));
      
      const targetDest = destinations.find(d => d.id === destinationId);
      notificationStore.success(`Created and added ${pendingVenueImport.venueName} to ${targetDest?.city || 'destination'}`, {
        title: 'Venue Created',
      });

      setDestinationModalOpen(false);
      setPendingVenueImport(null);
      onVenueCreated?.();
    } catch (error) {
      console.error('Failed to create venue:', error);
      notificationStore.error('Failed to create venue. Please try again.', {
        title: 'Error',
      });
    } finally {
      setIsCreatingWithDestination(false);
      if (pendingVenueImport) {
        stopProcessing(pendingVenueImport.cventSupplierId);
      }
    }
  }

  // Handle creating venue with a new destination
  async function handleCreateNewDestinationAndAdd(destination: { city: string; country: string; place_id?: string; destination_type?: 'city' | 'region' | 'country' }) {
    if (!pendingVenueImport) return;
    
    setIsCreatingWithDestination(true);
    
    try {
      const { CventIntegrationService } = await import('@/services/CventIntegrationService');
      const service = new CventIntegrationService();
      
      await service.createVenueFromCvent({
        eventId,
        cventSupplierId: pendingVenueImport.cventSupplierId,
      });

      setHiddenCventIds(prev => new Set(prev).add(pendingVenueImport.cventSupplierId));
      
      notificationStore.success(`Created ${destination.city} destination and added ${pendingVenueImport.venueName}`, {
        title: 'Venue Created',
      });

      setDestinationModalOpen(false);
      setPendingVenueImport(null);
      onVenueCreated?.();
    } catch (error) {
      console.error('Failed to create venue:', error);
      notificationStore.error('Failed to create venue. Please try again.', {
        title: 'Error',
      });
    } finally {
      setIsCreatingWithDestination(false);
      if (pendingVenueImport) {
        stopProcessing(pendingVenueImport.cventSupplierId);
      }
    }
  }

  function handleCloseDestinationModal() {
    if (pendingVenueImport) {
      stopProcessing(pendingVenueImport.cventSupplierId);
    }
    setDestinationModalOpen(false);
    setPendingVenueImport(null);
  }

  // If no unconnected venues, don't render anything
  if (unconnectedVenues.length === 0) {
    // Only show empty state if we also have no matched venues (handled by parent logic typically, but here for safety)
    if (matchResults.matched.length === 0) {
       return (
        <Card>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                No venues found in this RFP.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }
    return null;
  }

  return (
    <VStack gap={6}>
      <Card>
        <CardHeader>
          <HStack gap={2} align="center">
            <LinkIcon className="w-5 h-5 text-primary-600" />
            <CardTitle className="text-lg">
              Pending Connections ({unconnectedVenues.length})
            </CardTitle>
            <Badge variant="primary">{unconnectedVenues.length}</Badge>
          </HStack>
        </CardHeader>
        <CardContent>
          <VStack gap={4}>
            {unconnectedVenues.map(({ cventVenue, suggestedMatch }) => {
              const isProcessing = processingIds.has(cventVenue.id);
              
              return (
                <div
                  key={cventVenue.id}
                  className="border border-gray-200 rounded-lg p-4 bg-white"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left: Cvent Venue (Source) */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                          Cvent Source
                        </Badge>
                        {cventVenue.code && (
                          <span className="text-xs font-mono text-gray-400">#{cventVenue.code}</span>
                        )}
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {cventVenue.venueName}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {[
                            cventVenue.address?.address1,
                            cventVenue.address?.city,
                            cventVenue.address?.stateProvince,
                            cventVenue.address?.countryCode
                          ].filter(Boolean).join(', ') || 'Address not available'}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                        {cventVenue.totalSleepingRoom && (
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-4 h-4" />
                            <span>{cventVenue.totalSleepingRoom} rooms</span>
                          </div>
                        )}
                        {cventVenue.website && (
                          <div className="flex items-center gap-1.5">
                            <LinkIcon className="w-4 h-4" />
                            <a href={cventVenue.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              Website
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Connection Action */}
                    <div className="lg:border-l lg:pl-6 space-y-3">
                      {suggestedMatch ? (
                        // Case A: Has a suggested match
                        <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="primary" className="text-xs">
                                  Suggested Match
                                </Badge>
                                <span className={cn(
                                  "text-xs font-medium",
                                  suggestedMatch.confidence > 80 ? "text-success-600" : "text-warning-600"
                                )}>
                                  {formatConfidence(suggestedMatch.confidence)} confidence
                                </span>
                              </div>
                              <h4 className="font-medium text-gray-900">
                                {suggestedMatch.venue.property_name}
                              </h4>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {[
                                  suggestedMatch.venue.city,
                                  suggestedMatch.venue.country
                                ].filter(Boolean).join(', ')}
                                {suggestedMatch.distanceKm !== undefined && (
                                  <span className="ml-1 text-gray-400">
                                    ({formatDistance(suggestedMatch.distanceKm)} away)
                                  </span>
                                )}
                              </p>
                            </div>
                            {onViewVenueDetails && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => onViewVenueDetails(suggestedMatch.venue)}
                              >
                                View
                              </Button>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<Check className="w-4 h-4" />}
                              onClick={() => handleConnectVenue(cventVenue.id, suggestedMatch.venue.id, suggestedMatch.venue.property_name)}
                              isLoading={isProcessing}
                              disabled={isProcessing}
                            >
                              Confirm Connection
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              leftIcon={<Edit2 className="w-3.5 h-3.5" />}
                              onClick={() => {
                                setActiveCventId(cventVenue.id);
                                setActiveCventVenueName(cventVenue.venueName);
                                setSearchModalOpen(true);
                              }}
                              disabled={isProcessing}
                            >
                              Change Match
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCreateVenue(cventVenue.id, cventVenue.venueName)}
                              disabled={isProcessing}
                              className="text-muted-foreground hover:text-gray-900"
                            >
                              Ignore & Create New
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Case B: No suggested match
                        <div className="bg-gray-50 border border-gray-100 rounded-md p-3 flex flex-col justify-center min-h-[120px]">
                          <div className="flex items-center gap-2 text-gray-500 mb-3">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">No direct match found in database</span>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<Plus className="w-4 h-4" />}
                              onClick={() => handleCreateVenue(cventVenue.id, cventVenue.venueName)}
                              isLoading={isProcessing}
                              disabled={isProcessing}
                            >
                              Create & Connect
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              leftIcon={<Search className="w-3.5 h-3.5" />}
                              onClick={() => {
                                setActiveCventId(cventVenue.id);
                                setActiveCventVenueName(cventVenue.venueName);
                                setSearchModalOpen(true);
                              }}
                              disabled={isProcessing}
                            >
                              Find in Database
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </VStack>
        </CardContent>
      </Card>

      {/* Destination Selection Modal for Cvent Imports */}
      <MoveToDestinationModal
        isOpen={destinationModalOpen}
        onClose={handleCloseDestinationModal}
        onMove={handleCreateWithDestination}
        onCreateAndMove={handleCreateNewDestinationAndAdd}
        destinations={destinations}
        venueName={pendingVenueImport?.venueName || ''}
        allowCreateNew={true}
        isMoving={isCreatingWithDestination}
        suggestedNewDestination={
          pendingVenueImport?.geocodedLocation?.city && pendingVenueImport?.geocodedLocation?.country
            ? {
                city: pendingVenueImport.geocodedLocation.city,
                country: pendingVenueImport.geocodedLocation.country,
              }
            : undefined
        }
      />

      {/* Venue Search Modal for manual matching */}
      <VenueSearchModal
        isOpen={searchModalOpen}
        onClose={() => {
          setSearchModalOpen(false);
          setActiveCventId(null);
          setActiveCventVenueName(null);
        }}
        onSelect={handleSearchSelect}
        title="Find Matching Venue"
        eventId={eventId}
        cventSupplierId={activeCventId || undefined}
        initialSearchTerm={activeCventVenueName || undefined}
        onVenueImported={handleVenueImported}
      />
    </VStack>
  );
}
